import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { GraphNodeView } from './GraphNodeView';
import { LINK_TONES } from './graphColors';
import type { IGraphCanvasProps } from './IGraphCanvasProps';
import type { IGraphNode } from './IGraphNode';
import { relationsLayout } from './relationsLayout';

/**
 * Полотно навігатора зв'язків (прототип v4): SVG на всю площу; ребра першого рівня — тоном типу зв'язку з підписом посередині,
 * глибші — сірі; вузли можна перетягувати, клік ставить вузол у центр, коліщатко масштабує (0,4–2,5).
 */
export function GraphCanvas({ nodes, edges, centerId, zoom, onZoom, onSelect, typeLabel, linkLabel, layoutKey }: IGraphCanvasProps) {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 900, h: 560 });
  const [moved, setMoved] = useState<Record<string, { x: number; y: number }>>({});
  useEffect(() => setMoved({}), [layoutKey, centerId]);
  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => e && setSize({ w: Math.max(320, e.contentRect.width), h: Math.max(240, e.contentRect.height) }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const base = useMemo(() => relationsLayout(nodes, size.w, size.h), [nodes, size.w, size.h]);
  const at = (id: string) => moved[id] ?? base[id] ?? { x: size.w / 2, y: size.h / 2 };
  const drag = (e: PointerEvent, node: IGraphNode) => {
    e.preventDefault();
    const start = { x: e.clientX, y: e.clientY };
    const from = at(node.id);
    let dragged = false;
    const move = (ev: globalThis.PointerEvent) => {
      const dx = (ev.clientX - start.x) / zoom; const dy = (ev.clientY - start.y) / zoom;
      if (Math.abs(dx) + Math.abs(dy) > 3) dragged = true;
      if (dragged) setMoved((m) => ({ ...m, [node.id]: { x: from.x + dx, y: from.y + dy } }));
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (!dragged) onSelect(node); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const cx = size.w / 2; const cy = size.h / 2;
  return (
    <div ref={box} className="rec-nav__canvas" onWheel={(e) => onZoom(Math.min(2.5, Math.max(0.4, zoom * (e.deltaY < 0 ? 1.1 : 0.9))))}>
      <svg width={size.w} height={size.h} viewBox={`0 0 ${size.w} ${size.h}`} role="img">
        <g transform={`translate(${cx} ${cy}) scale(${zoom}) translate(${-cx} ${-cy})`}>
          {edges.map((e) => {
            const a = at(e.from); const b = at(e.to);
            const first = e.level === 1 && (e.from === centerId || e.to === centerId);
            return (
              <g key={`${e.from}|${e.to}`}>
                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={first ? LINK_TONES[e.type] ?? LINK_TONES['relates'] : 'var(--line2)'} strokeWidth={first ? 2.2 : 1} />
                {first && <text className="rec-nav__edge" x={(a.x + b.x) / 2} y={(a.y + b.y) / 2} textAnchor="middle" fill={LINK_TONES[e.type] ?? LINK_TONES['relates']}>{linkLabel(e.type)}</text>}
              </g>
            );
          })}
          {[...nodes].sort((p, q) => q.level - p.level).map((n) => (
            <GraphNodeView key={n.id} node={n} x={at(n.id).x} y={at(n.id).y} center={n.id === centerId} typeLabel={typeLabel(n)} onPointerDown={drag} />
          ))}
        </g>
      </svg>
    </div>
  );
}
